import useActiveChat from "@/composables/useActiveChat";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useMultiSelect from "@/composables/v2.1/useMultiSelect";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import MultiSelectChatInterface from "@/interfaces/multiSelectChat.interface";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";
import { resolveChatStatusFilter } from "@/utils/chats";
import { readonly, ref, Ref } from "@vue/composition-api";
import { isArray } from "lodash";
import { useToast } from "vue-toastification/composition";
import useChats from "../useChats";

interface OptionsInterface {
    showToast: boolean;
}

const isArchiveChatPending: Ref<boolean> = ref(false);

export default function useArchiveChat(options: OptionsInterface = { showToast: true }) {
    const { publicApiEndpointPath } = useInboxModuleType();
    const toast = useToast();
    const { closeActiveChat } = useActiveChat();

    const archiveChat = async (chat: MultiSelectChatInterface | MultiSelectChatInterface[]): Promise<void> => {
        const chats = isArray(chat) ? chat : [chat];
        if (!chats[0]) {
            return;
        }
        const { updateChatInChatsList } = useChats();

        const archiveResults: { ok: MultiSelectChatInterface[]; error: MultiSelectChatInterface[] } = {
            ok: [],
            error: [],
        };
        isArchiveChatPending.value = true;

        await Promise.all(
            chats.map(async (chat) => {
                if (!chat.id || !chat.channel?.id || !chat.identityId) {
                    throw new Error("No id, channelId or identityId passed.");
                }

                const { deleteUnreadChat } = useUnreadChats();
                deleteUnreadChat(chat.id);

                await Promise.all([
                    axios.post(`/${publicApiEndpointPath.value}/archive`, {
                        chatId: chat.id,
                        channelId: chat.channel.id,
                    }),
                    updateChatInChatsList(chat.id, {
                        statusFilter: resolveChatStatusFilter(
                            {
                                lastConversation: {
                                    status: "closed",
                                },
                            }
                            // teamsOfUser // @DENNIS 2023-03 This param was unused in the function itself, so commented.
                        ),
                    }),
                    closeActiveChat(chat.id),
                ])
                    .then(() => archiveResults.ok.push(chat))
                    .catch(() => archiveResults.error.push(chat));
            })
        );

        const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();
        if (multiSelectEnabled.value) {
            clearMultiSelect(archiveResults.ok);
        }

        if (options.showToast) {
            let status = "success";
            let notificationText = i18n.t("entities.conversation.is-archived-amount", {
                amount: archiveResults.ok.length,
                entity: i18n.tc("entities.conversation.conversations", archiveResults.ok.length),
            });

            if (archiveResults.ok.length > 0 && archiveResults.error.length > 0) {
                status = "warning";
                notificationText = i18n.t("archived-multiple-amount", {
                    amountOk: archiveResults.ok.length,
                    entityOk: i18n.tc("entities.conversation.conversations", archiveResults.ok.length),
                    amountError: archiveResults.error.length,
                    entityError: i18n.tc("entities.conversation.conversations", archiveResults.error.length),
                });
            } else if (archiveResults.ok.length === 0 && archiveResults.error.length > 0) {
                status = "error";
                notificationText = i18n.t("entities.conversation.could-not-archive-amount", {
                    amount: archiveResults.error.length,
                    entity: i18n.tc("entities.conversation.conversations", archiveResults.error.length),
                });
            }

            toast({
                component: toastificationContent,
                props: {
                    title: i18n.t("entities.conversation.Archive"),
                    text: notificationText,
                    icon: status === "error" ? "times" : "check",
                    variant: status,
                },
            });
        }

        isArchiveChatPending.value = false;
    };

    const unarchiveChat = () => {
        // @todo
    };

    return {
        archiveChat,
        unarchiveChat,
        isArchiveChatPending: readonly(isArchiveChatPending),
    };
}
